/**
 * EnvironmentSystem — runs the event rotation.
 *
 * Every 90–150 seconds we pick an event by `weight`, show a 20-second
 * warning banner via the event bus, then activate it for the event's
 * duration. Effects are exposed as a flat object that SpawnSystem,
 * MovementSystem, AbsorptionSystem etc. read each frame.
 *
 * Events are mostly buffs or neutral — Phase 1's tone is welcoming.
 */

import { bus } from '@/core/EventBus';
import type { EnvironmentalEventDef, EnvironmentEffect } from '@/data/types';
import { weightedPick, randRange } from '@/utils/rng';
import { saveManager } from '@/core/SaveManager';

type Phase = 'idle' | 'warning' | 'active';

export class EnvironmentSystem {
  private defs: EnvironmentalEventDef[];
  private phase: Phase = 'idle';
  private timer = 60; // initial delay before first event
  private active: EnvironmentalEventDef | null = null;
  private effects: EnvironmentEffect = {};

  constructor(defs: Record<string, EnvironmentalEventDef>) {
    this.defs = Object.values(defs);
  }

  getEffects(): EnvironmentEffect { return this.effects; }

  currentEvent(): EnvironmentalEventDef | null { return this.active; }

  update(dt: number): void {
    this.timer -= dt;

    if (this.phase === 'idle' && this.timer <= 0) {
      const pick = weightedPick(this.defs, 'weight');
      if (pick) {
        this.phase = 'warning';
        this.active = pick;
        this.timer = 20; // warning duration
        bus.emit('event:warning', { eventId: pick.id, secondsUntil: 20 });
      } else {
        this.timer = 60;
      }
      return;
    }

    if (this.phase === 'warning' && this.timer <= 0) {
      if (this.active) {
        this.effects = this.active.effects;
        this.phase = 'active';
        this.timer = this.active.duration;
        bus.emit('event:started', { eventId: this.active.id });
        bus.emit('audio:cue', { id: 'event' });
        if (this.active.codexId) bus.emit('codex:unlocked', { entryId: this.active.codexId });
      }
      return;
    }

    if (this.phase === 'active' && this.timer <= 0 && this.active) {
      bus.emit('event:ended', { eventId: this.active.id });
      saveManager.getRun().eventsSurvived += 1;
      this.active = null;
      this.effects = {};
      this.phase = 'idle';
      this.timer = randRange(90, 150);
    }
  }

  /** For the HUD: how long until the next phase change. */
  timeLeft(): number { return this.timer; }
  getPhase(): Phase { return this.phase; }
}
